import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Plus, Trash2, Loader2, FileText, AlertCircle, Wallet, Receipt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';
import ProductPicker from '../../../components/admin/ProductPicker';
import PromoteItemToProductModal from '../../../components/admin/PromoteItemToProductModal';
import RecordPaymentModal from '../../../components/admin/finance/RecordPaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyItem = () => ({
  product_id: null,
  product_sku: '',
  description_en: '',
  quantity: 1,
  unit: 'units',
  unit_price: 0,
  tax_rate: 0,
  discount: 0,
});

const emptyParty = () => ({ name: '', tax_id: '', tax_id_label: 'NIF', address: '', phone: '', email: '' });

const PARTY_LABELS = {
  importer: 'Importer',
  consignee_hc: 'Consignee HC',
  consignee_bl: 'Consignee BL',
  notify_party: 'Notify Party',
};

const InvoiceEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [promoteIdx, setPromoteIdx] = useState(null); // line item being promoted to Master Registry
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const [form, setForm] = useState({
    client_name: '',
    client_email: '',
    client_address: '',
    client_trn: '',
    issue_date: '',
    due_date: '',
    currency: 'AED',
    items: [],
    notes: '',
    terms: '',
    // Payment options offered — SYNCED with Procurement Invoice module
    payment_methods: ['bank_transfer'],
    payment_link_url: '',
    payment_link_provider: 'Network International',
    payment_link_label: '',
    bank_account_snapshot: null,
    // Cost & Margin Breakdown (internal only)
    cost_base: 0,
    operational_cost_mode: 'percentage',
    operational_cost_value: 0,
    markup_mode: 'percentage',
    markup_value: 0,
    fta_tax_rate: 9.0,
  });

  useEffect(() => {
    fetchInvoice();
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/finance/invoices/${id}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(r.data || []);
    } catch (_) {
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fmtMoney = (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: form.currency || 'AED',
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(
      `Reverse installment of ${fmtMoney(payment.amount)} ` +
      `dated ${payment.payment_date}?\n\n` +
      `The invoice balance, paid amount and status will be recalculated automatically.\n\n` +
      `This action cannot be undone.`
    )) return;
    try {
      await axios.delete(`${API_URL}/api/finance/payments/${payment.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Installment reversed', description: 'Invoice balance recalculated.' });
      await fetchInvoice();
      await fetchPayments();
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Reversal failed',
        description: e.response?.data?.detail || 'Could not delete installment.',
      });
    }
  };

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/finance/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const inv = resp.data;
      setInvoice(inv);
      setForm({
        client_name: inv.client_name || inv.client_snapshot?.name || '',
        client_email: inv.client_email || inv.client_snapshot?.email || '',
        client_address: inv.client_address || inv.client_snapshot?.address || '',
        client_trn: inv.client_trn || inv.client_snapshot?.trn || '',
        issue_date: (inv.issue_date || inv.invoice_date || '').slice(0, 10),
        due_date: (inv.due_date || '').slice(0, 10),
        currency: inv.currency || 'AED',
        items: (inv.items || []).map((it) => ({
          description_en: it.description_en || it.description || it.product_name || '',
          quantity: Number(it.quantity) || 0,
          unit: it.unit || 'units',
          unit_price: Number(it.unit_price) || 0,
          tax_rate: Number(it.tax_rate) || 0,
          discount: Number(it.discount) || 0,
        })),
        notes: inv.notes || '',
        terms: inv.terms || '',
        payment_methods: Array.isArray(inv.payment_methods) && inv.payment_methods.length
          ? inv.payment_methods
          : ['bank_transfer'],
        payment_link_url: inv.payment_link_url || '',
        payment_link_provider: inv.payment_link_provider || 'Network International',
        payment_link_label: inv.payment_link_label || '',
        bank_account_snapshot: inv.bank_account_snapshot || null,
        cost_base: Number(inv.cost_base) || 0,
        operational_cost_mode: inv.operational_cost_mode || 'percentage',
        operational_cost_value: Number(inv.operational_cost_value) || 0,
        markup_mode: inv.markup_mode || 'percentage',
        markup_value: Number(inv.markup_value) || 0,
        import_parties: {
          importer: { ...emptyParty(), ...(inv.import_parties?.importer || {}) },
          consignee_hc: { ...emptyParty(), ...(inv.import_parties?.consignee_hc || {}) },
          consignee_bl: { ...emptyParty(), ...(inv.import_parties?.consignee_bl || {}) },
          notify_party: { ...emptyParty(), ...(inv.import_parties?.notify_party || {}) },
        },
      });
    } catch (err) {
      toast({ variant: 'destructive', title: 'Error', description: 'Falha ao carregar factura' });
      navigate('/admin/finance/invoices');
    } finally {
      setLoading(false);
    }
  };

  const isEditable = useMemo(() => {
    const status = (invoice?.status || '').toLowerCase();
    return status === 'draft' || status === 'pending' || status === '';
  }, [invoice]);

  // Live totals (match backend recompute logic)
  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    let discount = 0;
    form.items.forEach((it) => {
      const sub = Number(it.quantity || 0) * Number(it.unit_price || 0);
      const disc = sub * (Number(it.discount || 0) / 100);
      const t = (sub - disc) * (Number(it.tax_rate || 0) / 100);
      subtotal += sub;
      discount += disc;
      tax += t;
    });
    const total = subtotal + tax - discount;
    return { subtotal, tax, discount, total };
  }, [form.items]);

  // Cost & Margin breakdown (live preview)
  // F-Margin-2 — the unit_price on the invoice ALREADY includes our markup
  // and operational costs. Markup is therefore DERIVED, not summed:
  //   net_revenue = subtotal − discount   (matches what the client pays, pre-tax)
  //   op_amount   = cost × op% (or fixed)
  //   markup      = net_revenue − cost − op_amount
  //   margin %    = markup / net_revenue × 100
  const breakdown = useMemo(() => {
    const cost = Number(form.cost_base) || 0;
    const opMode = form.operational_cost_mode || 'percentage';
    const opValue = Number(form.operational_cost_value) || 0;
    const ftaRate = Number(form.fta_tax_rate) || 9;
    const opAmount = opMode === 'fixed' ? opValue : cost * (opValue / 100);
    const netRevenue = totals.subtotal - totals.discount;
    const totalCost = cost + opAmount;
    const mkAmount = netRevenue - totalCost;
    const salePrice = netRevenue;
    const marginPct = netRevenue > 0 ? (mkAmount / netRevenue) * 100 : 0;
    const ftaTax = Math.max(0, mkAmount) * (ftaRate / 100);
    const netProfit = mkAmount - ftaTax;
    return { cost, opAmount, mkAmount, totalCost, salePrice, marginPct, ftaTax, netProfit, ftaRate };
  }, [form.cost_base, form.operational_cost_mode, form.operational_cost_value, form.fta_tax_rate, totals.subtotal, totals.discount]);

  const updateField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  // Payment terms preset: auto-set due_date from issue_date + N days
  const applyPaymentTermsPreset = (days) => {
    if (!form.issue_date) return;
    const d = new Date(form.issue_date);
    d.setDate(d.getDate() + Number(days));
    const iso = d.toISOString().slice(0, 10);
    setForm((p) => ({ ...p, due_date: iso }));
  };

  const updateParty = (partyKey, field, val) =>
    setForm((p) => ({
      ...p,
      import_parties: {
        ...p.import_parties,
        [partyKey]: { ...(p.import_parties?.[partyKey] || emptyParty()), [field]: val },
      },
    }));

  const copyPartyFromClient = (partyKey) => {
    setForm((p) => ({
      ...p,
      import_parties: {
        ...p.import_parties,
        [partyKey]: {
          name: p.client_name,
          tax_id: p.client_trn,
          tax_id_label: (p.import_parties?.[partyKey]?.tax_id_label) || 'NIF',
          address: p.client_address,
          phone: p.import_parties?.[partyKey]?.phone || '',
          email: p.client_email,
        },
      },
    }));
  };
  const updateItem = (idx, key, val) =>
    setForm((p) => ({
      ...p,
      items: p.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)),
    }));

  // F-UnifiedProducts — auto-fill line from Master Product Registry on edit.
  const handleItemProductPick = (idx, product) => {
    if (!product) return;
    setForm((p) => ({
      ...p,
      items: p.items.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it };
        next.product_id = product.id;
        next.product_sku = product.sku || '';
        if (!next.description_en) next.description_en = product.name || '';
        const price = product.selling_price ?? product.default_price;
        if (price != null && (!next.unit_price || Number(next.unit_price) === 0)) {
          next.unit_price = Number(price) || 0;
        }
        if (product.default_tax_rate != null) {
          next.tax_rate = Number(product.default_tax_rate) || next.tax_rate;
        }
        if (product.unit && (!next.unit || next.unit === 'units')) {
          next.unit = product.unit;
        }
        return next;
      }),
    }));
    toast({
      title: 'Product loaded',
      description: `${product.sku || ''} — ${product.name || ''} (editable below).`,
    });
  };
  const updateBank = (key, val) =>
    setForm((p) => ({
      ...p,
      bank_account_snapshot: { ...(p.bank_account_snapshot || {}), [key]: val },
    }));
  const addItem = () => setForm((p) => ({ ...p, items: [...p.items, emptyItem()] }));
  const removeItem = (idx) =>
    setForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        client_name: form.client_name,
        client_email: form.client_email,
        client_address: form.client_address,
        client_trn: form.client_trn,
        issue_date: form.issue_date,
        due_date: form.due_date,
        currency: form.currency,
        items: form.items.map((it) => ({
          product_id: it.product_id || null,
          product_sku: it.product_sku || '',
          description_en: it.description_en,
          description: it.description_en,
          quantity: Number(it.quantity || 0),
          unit: it.unit,
          unit_price: Number(it.unit_price || 0),
          tax_rate: Number(it.tax_rate || 0),
          discount: Number(it.discount || 0),
        })),
        notes: form.notes,
        terms: form.terms,
        // Payment options (SYNCED across Finance & Procurement invoices)
        payment_methods: (form.payment_methods && form.payment_methods.length)
          ? form.payment_methods : ['bank_transfer'],
        payment_link_url: (form.payment_methods || []).includes('payment_link')
          ? (form.payment_link_url || null) : null,
        payment_link_provider: (form.payment_methods || []).includes('payment_link')
          ? (form.payment_link_provider || 'Network International') : null,
        payment_link_label: (form.payment_methods || []).includes('payment_link')
          ? (form.payment_link_label || null) : null,
      };
      if (form.bank_account_snapshot) {
        payload.bank_account_snapshot = form.bank_account_snapshot;
      }
      // Cost & Margin Breakdown fields
      payload.cost_base = Number(form.cost_base) || 0;
      payload.operational_cost_mode = form.operational_cost_mode;
      payload.operational_cost_value = Number(form.operational_cost_value) || 0;
      payload.markup_mode = form.markup_mode;
      payload.markup_value = Number(form.markup_value) || 0;
      payload.fta_tax_rate = Number(form.fta_tax_rate) || 0;
      // Only send import_parties if at least one field is filled
      const anyPartyFilled = Object.values(form.import_parties || {}).some((p) =>
        p && (p.name || p.tax_id || p.address || p.phone || p.email)
      );
      if (anyPartyFilled) {
        payload.import_parties = form.import_parties;
      }
      if (!isEditable) {
        // Strip amount-related fields when non-editable status
        delete payload.items;
        delete payload.currency;
      }
      const resp = await axios.patch(
        `${API_URL}/api/finance/invoices/${id}/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data.success) {
        toast({ title: 'Guardado', description: 'Factura actualizada com sucesso' });
        navigate(`/admin/finance/invoices/${id}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro ao guardar';
      toast({ variant: 'destructive', title: 'Error', description: msg });
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: form.currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="invoice-edit-page">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/admin/finance/invoices/${id}`)}
          className="flex items-center text-[#6C757D] hover:text-[#2B2D42] mb-4"
          data-testid="back-to-detail"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar ao detalhe
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
              <FileText className="h-6 w-6 text-[#00B4D8]" />
              Editar factura {invoice?.invoice_number}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={isEditable ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                {invoice?.status?.toUpperCase() || 'DRAFT'}
              </Badge>
              {!isEditable && (
                <span className="text-xs text-amber-700 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Montantes bloqueados (só podem ser editados em status DRAFT)
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/admin/finance/invoices/${id}`)} data-testid="cancel-edit">
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#00B4D8] hover:bg-[#0096c7]"
              data-testid="save-invoice"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: client + items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client */}
          <Card>
            <CardHeader><CardTitle className="text-base">Cliente (Bill To)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">Nome</label>
                  <input
                    type="text"
                    value={form.client_name}
                    onChange={(e) => updateField('client_name', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                    data-testid="edit-client-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Email</label>
                  <input
                    type="email"
                    value={form.client_email}
                    onChange={(e) => updateField('client_email', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-600">Morada</label>
                  <input
                    type="text"
                    value={form.client_address}
                    onChange={(e) => updateField('client_address', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">TRN / NIF</label>
                  <input
                    type="text"
                    value={form.client_trn}
                    onChange={(e) => updateField('client_trn', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Currency</label>
                  <select
                    value={form.currency}
                    onChange={(e) => updateField('currency', e.target.value)}
                    disabled={!isEditable}
                    className="mt-1 w-full px-3 py-2 border rounded-lg disabled:bg-gray-100"
                  >
                    {['USD', 'AED', 'EUR', 'GBP', 'AOA', 'BRL'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">Issue Date</label>
                  <input
                    type="date"
                    value={form.issue_date}
                    onChange={(e) => updateField('issue_date', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Due Date</label>
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => updateField('due_date', e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                    data-testid="due-date-input"
                  />
                </div>
              </div>
              {/* Quick payment terms presets */}
              <div className="flex flex-wrap gap-2 items-center pt-2">
                <span className="text-xs font-medium text-gray-600">Quick Terms:</span>
                {[3, 7, 15, 30, 45, 60].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => applyPaymentTermsPreset(d)}
                    className="text-xs px-3 py-1 border border-gray-300 hover:border-[#00B4D8] hover:bg-[#00B4D8]/10 hover:text-[#00B4D8] text-gray-700 rounded-full transition"
                    data-testid={`preset-net${d}`}
                  >
                    Net {d}
                  </button>
                ))}
                <span className="text-[11px] text-gray-500 ml-1">Sets Due Date = Issue Date + N days</span>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Items ({form.items.length})</CardTitle>
              {isEditable && (
                <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {form.items.length === 0 && (
                <div className="text-center text-sm text-gray-500 py-4">Sem items. Clique "Adicionar".</div>
              )}
              {form.items.map((it, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-gray-50" data-testid={`item-row-${idx}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-bold text-gray-500 mt-3 w-6">#{idx + 1}</span>
                    <div className="flex-1 grid grid-cols-12 gap-2">
                      {isEditable && (
                        <div className="col-span-12">
                          <label className="text-xs text-gray-600">
                            Product from Master Registry{' '}
                            <span className="text-gray-400">(auto-fills description / price / tax)</span>
                          </label>
                          <div className="mt-1">
                            <ProductPicker
                              value={it.product_id || null}
                              onSelect={(p) => handleItemProductPick(idx, p)}
                              placeholder="Search SKU or product name…"
                            />
                          </div>
                          {it.product_sku && (
                            <div className="text-[11px] text-[#00B4D8] mt-1">
                              Linked SKU: <code className="bg-white px-1 py-0.5 rounded border">{it.product_sku}</code>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="col-span-12">
                        <label className="text-xs text-gray-600">Descrição</label>
                        <input
                          type="text"
                          value={it.description_en}
                          onChange={(e) => updateItem(idx, 'description_en', e.target.value)}
                          disabled={!isEditable}
                          className="mt-1 w-full px-3 py-2 border rounded text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div className="col-span-3">
                        <label className="text-xs text-gray-600">Qty</label>
                        <input
                          type="number"
                          step="0.01"
                          value={it.quantity}
                          onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                          disabled={!isEditable}
                          className="mt-1 w-full px-2 py-2 border rounded text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-600">Unit</label>
                        <input
                          type="text"
                          value={it.unit}
                          onChange={(e) => updateItem(idx, 'unit', e.target.value)}
                          disabled={!isEditable}
                          className="mt-1 w-full px-2 py-2 border rounded text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div className="col-span-3">
                        <label className="text-xs text-gray-600">Unit Price</label>
                        <input
                          type="number"
                          step="0.01"
                          value={it.unit_price}
                          onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                          disabled={!isEditable}
                          className="mt-1 w-full px-2 py-2 border rounded text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-600">Tax %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={it.tax_rate}
                          onChange={(e) => updateItem(idx, 'tax_rate', e.target.value)}
                          disabled={!isEditable}
                          className="mt-1 w-full px-2 py-2 border rounded text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-600">Disc %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={it.discount}
                          onChange={(e) => updateItem(idx, 'discount', e.target.value)}
                          disabled={!isEditable}
                          className="mt-1 w-full px-2 py-2 border rounded text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div className="col-span-12 text-right text-sm font-semibold text-[#00B4D8]">
                        Total:{' '}
                        {fmt(
                          (Number(it.quantity) || 0) *
                            (Number(it.unit_price) || 0) *
                            (1 - (Number(it.discount) || 0) / 100) *
                            (1 + (Number(it.tax_rate) || 0) / 100)
                        )}
                      </div>
                    </div>
                    {isEditable && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-800 mt-4"
                        onClick={() => removeItem(idx)}
                        data-testid={`remove-item-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {isEditable && !it.product_id && (it.description_en || '').trim().length >= 2 && (
                    <div className="mt-2 pl-8">
                      <button
                        type="button"
                        onClick={() => setPromoteIdx(idx)}
                        className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 transition"
                        title="Promote this ad-hoc item to the Master Product Registry for reuse"
                        data-testid={`promote-item-${idx}`}
                      >
                        <Plus className="w-3 h-3" /> Save as Product / Service
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Shipment Parties (Import Invoices) */}
          <Card className="border-blue-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span>🚢</span> Shipment Parties
                <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full ml-1">Import</span>
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">Preenchimento opcional — usado em facturas de importação. Só aparece no PDF quando preenchido.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(PARTY_LABELS).map(([partyKey, partyLabel]) => {
                const party = form.import_parties?.[partyKey] || emptyParty();
                return (
                  <div key={partyKey} className="border rounded-lg p-4 bg-blue-50/30">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-semibold text-[#023E8A]">{partyLabel}</h4>
                      <button
                        type="button"
                        onClick={() => copyPartyFromClient(partyKey)}
                        className="text-[11px] px-2.5 py-1 border border-blue-300 text-blue-700 hover:bg-blue-100 rounded transition"
                        data-testid={`copy-from-client-${partyKey}`}
                      >
                        ↓ Copiar do Cliente
                      </button>
                    </div>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-12 md:col-span-6">
                        <label className="text-[11px] text-gray-600">Name / Company</label>
                        <input
                          type="text"
                          value={party.name}
                          onChange={(e) => updateParty(partyKey, 'name', e.target.value)}
                          className="mt-1 w-full px-3 py-2 border rounded text-sm"
                          placeholder="TRANZERNAGA SU"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <label className="text-[11px] text-gray-600">Tax Label</label>
                        <select
                          value={party.tax_id_label || 'NIF'}
                          onChange={(e) => updateParty(partyKey, 'tax_id_label', e.target.value)}
                          className="mt-1 w-full px-2 py-2 border rounded text-sm bg-white"
                        >
                          <option>NIF</option>
                          <option>TRN</option>
                          <option>CNPJ</option>
                          <option>VAT</option>
                          <option>Tax ID</option>
                        </select>
                      </div>
                      <div className="col-span-8 md:col-span-4">
                        <label className="text-[11px] text-gray-600">Tax ID Value</label>
                        <input
                          type="text"
                          value={party.tax_id}
                          onChange={(e) => updateParty(partyKey, 'tax_id', e.target.value)}
                          className="mt-1 w-full px-3 py-2 border rounded text-sm"
                          placeholder="5001025040"
                        />
                      </div>
                      <div className="col-span-12">
                        <label className="text-[11px] text-gray-600">Address</label>
                        <input
                          type="text"
                          value={party.address}
                          onChange={(e) => updateParty(partyKey, 'address', e.target.value)}
                          className="mt-1 w-full px-3 py-2 border rounded text-sm"
                          placeholder="AV. FIDEL DE CASTRO RUIZ S/N - LUANDA - ANGOLA"
                        />
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <label className="text-[11px] text-gray-600">Telephone</label>
                        <input
                          type="text"
                          value={party.phone}
                          onChange={(e) => updateParty(partyKey, 'phone', e.target.value)}
                          className="mt-1 w-full px-3 py-2 border rounded text-sm"
                          placeholder="+244 9XX XXX XXX"
                        />
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <label className="text-[11px] text-gray-600">Email</label>
                        <input
                          type="email"
                          value={party.email}
                          onChange={(e) => updateParty(partyKey, 'email', e.target.value)}
                          className="mt-1 w-full px-3 py-2 border rounded text-sm"
                          placeholder="geral@example.com"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Payments & Balance — visible for issued / partial / paid invoices only. */}
          {(() => {
            const status = (invoice?.status || '').toLowerCase();
            const issued = status && status !== 'draft' && status !== 'pro_forma' && status !== 'proforma'
              && status !== 'cancelled' && status !== 'void';
            if (!issued) {
              if (status === 'draft' || status === 'pro_forma' || status === 'proforma') {
                return (
                  <Card data-testid="payments-locked-card">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3 text-sm bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <Wallet className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
                        <div>
                          <div className="font-semibold text-amber-900">Pagamentos parcelados</div>
                          <div className="text-amber-800">
                            A registar instalments fica disponível assim que esta factura for emitida
                            (Issue as Tax Invoice / Pro-Forma). Drafts não aceitam recebimentos.
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              return null;
            }
            const total = Number(invoice.total) || 0;
            const paid = Number(invoice.amount_paid) || 0;
            const balance = Math.max(0, Math.round(((invoice.balance_due != null ? Number(invoice.balance_due) : total - paid)) * 100) / 100);
            const fullyPaid = balance <= 0.01 && total > 0;
            const pct = total > 0 ? Math.min(100, (paid / total) * 100) : 0;
            return (
              <Card data-testid="payments-card">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-[#2B2D42] flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-[#00B4D8]" />
                        Payments &amp; Balance
                      </h3>
                      <p className="text-xs text-[#6C757D] mt-0.5">
                        Record partial or full installments. The invoice closes automatically when the balance reaches zero.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      type="button"
                      onClick={() => setPaymentModalOpen(true)}
                      disabled={fullyPaid}
                      className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
                      data-testid="record-payment-btn"
                    >
                      <Wallet className="w-4 h-4 mr-1" />
                      {fullyPaid ? 'Fully paid' : 'Record Payment'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-3 text-center">
                    <div className="bg-gray-50 rounded-lg p-2 border">
                      <div className="text-[10px] uppercase text-gray-500 tracking-wider">Total</div>
                      <div className="text-sm font-bold text-[#2B2D42]">{fmtMoney(total)}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 border border-green-200">
                      <div className="text-[10px] uppercase text-green-700 tracking-wider">Paid</div>
                      <div className="text-sm font-bold text-green-700">{fmtMoney(paid)}</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                      <div className="text-[10px] uppercase text-amber-700 tracking-wider">Outstanding</div>
                      <div className="text-sm font-bold text-amber-700">{fmtMoney(balance)}</div>
                    </div>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mb-1">
                    <div className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
                         style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[11px] text-gray-500 text-right">{pct.toFixed(1)}% paid</div>

                  <div className="mt-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                      Installment History ({payments.length})
                    </h4>
                    {paymentsLoading ? (
                      <div className="text-xs text-gray-500 py-4 text-center">Loading…</div>
                    ) : payments.length === 0 ? (
                      <div className="text-xs text-gray-500 py-4 text-center bg-gray-50 rounded-lg border border-dashed">
                        No payments recorded yet. Use <strong>Record Payment</strong> to register an installment.
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
                            <tr>
                              <th className="px-3 py-2 text-left">#</th>
                              <th className="px-3 py-2 text-left">Date</th>
                              <th className="px-3 py-2 text-left">Method</th>
                              <th className="px-3 py-2 text-left">Reference</th>
                              <th className="px-3 py-2 text-right">Amount</th>
                              <th className="px-3 py-2" />
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {payments.map((p, idx) => (
                              <tr key={p.id} data-testid={`payment-row-${idx}`}>
                                <td className="px-3 py-2 text-xs text-gray-500">#{idx + 1}</td>
                                <td className="px-3 py-2 text-sm">{p.payment_date}</td>
                                <td className="px-3 py-2 text-sm capitalize">{(p.payment_method || '').replace('_', ' ')}</td>
                                <td className="px-3 py-2 text-xs text-gray-600">{p.reference || '—'}</td>
                                <td className="px-3 py-2 text-sm text-right font-semibold text-green-700">
                                  {fmtMoney(p.amount)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <div className="inline-flex gap-1">
                                    <a
                                      href={`${API_URL}/api/finance/payments/${p.id}/receipt.pdf`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="p-1.5 rounded border bg-white hover:bg-cyan-50 text-[#00B4D8]"
                                      title="Download payment receipt (PDF for client)"
                                      data-testid={`payment-receipt-${idx}`}
                                    >
                                      <Receipt className="w-3.5 h-3.5" />
                                    </a>
                                    <button
                                      type="button"
                                      onClick={() => handleDeletePayment(p)}
                                      className="p-1.5 rounded border bg-white hover:bg-red-50 text-red-600"
                                      title="Reverse installment (recalculates balance)"
                                      data-testid={`payment-delete-${idx}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Payment Options — SYNCED with Procurement Invoice */}
          <Card>
            <CardHeader><CardTitle className="text-base">Payment Options Offered</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { value: 'bank_transfer', label: 'Bank Transfer', hint: 'Uses the selected bank account snapshot.' },
                  { value: 'payment_link', label: 'Payment Link (card / wallet)', hint: 'Hosted checkout — Network, Stripe Payment Link, PayPal.me…' },
                ].map((opt) => {
                  const checked = (form.payment_methods || []).includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
                        checked ? 'border-[#023E8A] bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid={`finance-edit-pm-${opt.value}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        className="mt-0.5 accent-[#023E8A]"
                        onChange={(e) => {
                          const set = new Set(form.payment_methods || []);
                          if (e.target.checked) set.add(opt.value); else set.delete(opt.value);
                          const arr = Array.from(set);
                          updateField('payment_methods', arr.length ? arr : ['bank_transfer']);
                        }}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-bold text-[#2B2D42]">{opt.label}</span>
                        <span className="block text-[11px] text-[#6C757D] leading-snug">{opt.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>

              {(form.payment_methods || []).includes('payment_link') && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-gray-600">Payment link URL *</label>
                    <input
                      type="url"
                      value={form.payment_link_url || ''}
                      onChange={(e) => updateField('payment_link_url', e.target.value)}
                      placeholder="https://checkout.network.ae/…"
                      className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                      data-testid="finance-edit-payment-link-url"
                    />
                    <div className="text-[10px] text-[#94a3b8] mt-1">
                      Paste from{' '}
                      <a href="https://www.network.ae/en/merchant-solutions/ecommerce-payments" target="_blank" rel="noreferrer" className="text-[#023E8A] underline">
                        network.ae Merchant Solutions
                      </a>{' '}
                      or any provider.
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600">Provider</label>
                    <input
                      type="text"
                      value={form.payment_link_provider || ''}
                      onChange={(e) => updateField('payment_link_provider', e.target.value)}
                      placeholder="Network International"
                      className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                      data-testid="finance-edit-payment-link-provider"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes + Terms */}
          <Card>
            <CardHeader><CardTitle className="text-base">Notas & Termos</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600">Notes (visível na factura)</label>
                <textarea
                  rows={4}
                  value={form.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Payment Terms</label>
                <textarea
                  rows={4}
                  value={form.terms}
                  onChange={(e) => updateField('terms', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column: totals + bank */}
        <div className="space-y-6">
          {/* Totals preview */}
          <Card className="sticky top-6">
            <CardHeader><CardTitle className="text-base">Resumo (preview)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Row label="Subtotal" value={fmt(totals.subtotal)} />
              {totals.discount > 0 && <Row label="Discount" value={`- ${fmt(totals.discount)}`} />}
              {totals.tax > 0 && <Row label="VAT / Tax" value={fmt(totals.tax)} />}
              <div className="border-t pt-3 flex justify-between items-center">
                <span className="font-bold text-[#2B2D42]">TOTAL</span>
                <span className="text-xl font-bold text-[#00B4D8]">{fmt(totals.total)}</span>
              </div>
              <div className="text-[11px] text-gray-500 leading-snug">
                Totais recalculados ao vivo. Serão persistidos ao clicar <strong>Guardar</strong>.
              </div>
            </CardContent>
          </Card>

          {/* Bank account */}
          <Card>
            <CardHeader><CardTitle className="text-base">Conta Bancária (snapshot)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {['bank_name', 'account_name', 'account_number', 'iban', 'swift', 'branch', 'currency'].map((k) => (
                <div key={k}>
                  <label className="text-xs font-medium text-gray-600 capitalize">{k.replace('_', ' ')}</label>
                  <input
                    type="text"
                    value={form.bank_account_snapshot?.[k] || ''}
                    onChange={(e) => updateBank(k, e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                    data-testid={`bank-${k}`}
                  />
                </div>
              ))}
              <div className="text-[11px] text-gray-500 leading-snug pt-2 border-t">
                Edição do snapshot apenas desta factura. Não altera a conta bancária da empresa.
              </div>
            </CardContent>
          </Card>

          {/* Cost & Margin Breakdown (INTERNAL) */}
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="text-amber-700">🔒</span> Cost &amp; Margin Breakdown
                <span className="text-[10px] uppercase tracking-wider bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full ml-1">Internal</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[11px] text-gray-600 leading-relaxed">
                Não aparece no PDF do cliente. Usado para relatórios P&amp;L e auditoria de margens.
              </p>

              {/* Cost base */}
              <div>
                <label className="text-xs font-medium text-gray-700">Cost Base (custo fornecedor)</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">{form.currency}</span>
                  <input
                    type="number"
                    step="0.01"
                    value={form.cost_base}
                    onChange={(e) => updateField('cost_base', e.target.value)}
                    className="w-full pl-12 pr-3 py-2 border rounded-lg text-sm font-mono"
                    data-testid="cost-base-input"
                  />
                </div>
              </div>

              {/* Operational cost */}
              <div>
                <label className="text-xs font-medium text-gray-700">Operational Cost</label>
                <div className="mt-1 flex gap-2">
                  <select
                    value={form.operational_cost_mode}
                    onChange={(e) => updateField('operational_cost_mode', e.target.value)}
                    className="px-2 py-2 border rounded-lg text-sm bg-white"
                    data-testid="op-cost-mode"
                  >
                    <option value="percentage">%</option>
                    <option value="fixed">{form.currency}</option>
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    value={form.operational_cost_value}
                    onChange={(e) => updateField('operational_cost_value', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                    data-testid="op-cost-value"
                  />
                </div>
                <div className="text-[11px] text-gray-500 mt-1">
                  = {fmt(breakdown.opAmount)}
                </div>
              </div>

              {/* Markup (derived from invoice — read-only) */}
              <div>
                <label className="text-xs font-medium text-gray-700">Markup / Profit (derived)</label>
                <div className="mt-1 px-3 py-2 border rounded-lg bg-gray-50 text-sm font-mono">
                  {fmt(breakdown.mkAmount)}
                </div>
                <div className="text-[11px] text-gray-500 mt-1">
                  = Net Revenue − Cost − Op. Cost · {breakdown.marginPct.toFixed(2)}% margin
                </div>
              </div>

              {/* Summary */}
              <div className="border-t pt-3 space-y-1.5">
                <Row label="Net Revenue (after discount)" value={fmt(breakdown.salePrice)} />
                <Row label="− Cost base" value={fmt(breakdown.cost)} />
                <Row label="− Op. Cost" value={fmt(breakdown.opAmount)} />
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className={`font-bold ${breakdown.mkAmount < 0 ? 'text-red-700' : 'text-amber-800'}`}>= Gross Profit</span>
                  <span className={`font-bold ${breakdown.mkAmount < 0 ? 'text-red-700' : 'text-amber-800'}`}>{fmt(breakdown.mkAmount)}</span>
                </div>
              </div>

              {/* FTA Tax (UAE Federal Tax Authority) */}
              <div className="border-t pt-3">
                <label className="text-xs font-medium text-gray-700">
                  FTA Corporate Tax Rate
                  <span className="text-[10px] text-gray-500 ml-1">(UAE Federal Tax Authority)</span>
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="number"
                    step="0.01"
                    value={form.fta_tax_rate}
                    onChange={(e) => updateField('fta_tax_rate', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                    data-testid="fta-tax-rate"
                  />
                  <span className="text-sm font-bold text-gray-700">%</span>
                </div>
                <div className="text-[11px] text-gray-500 mt-1">
                  Default 9% (UAE corporate tax on profits). Edit per invoice if needed.
                </div>
                <div className="mt-3 space-y-1.5 border-t pt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-red-700">- FTA Tax ({breakdown.ftaRate.toFixed(2)}%)</span>
                    <span className="text-red-700 font-semibold">{fmt(breakdown.ftaTax)}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span className="font-bold text-green-700">= NET PROFIT</span>
                    <span className="font-bold text-green-700 text-base">{fmt(breakdown.netProfit)}</span>
                  </div>
                </div>
              </div>

              <div className="text-[11px] text-gray-500 leading-snug pt-2 border-t">
                Este breakdown é persistido com a factura e disponível nos relatórios.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PromoteItemToProductModal
        isOpen={promoteIdx !== null}
        item={promoteIdx !== null ? form.items[promoteIdx] : null}
        currency={form.currency}
        onClose={() => setPromoteIdx(null)}
        onPromoted={(product) => {
          if (promoteIdx === null) return;
          setForm((p) => ({
            ...p,
            items: p.items.map((it, i) => i === promoteIdx
              ? {
                  ...it,
                  product_id: product.id,
                  product_sku: product.sku,
                  // Keep operator-entered description/price; only sync if blank.
                  description_en: it.description_en || product.name || '',
                }
              : it),
          }));
          setPromoteIdx(null);
        }}
      />

      <RecordPaymentModal
        isOpen={paymentModalOpen}
        invoice={invoice}
        onClose={() => setPaymentModalOpen(false)}
        onRecorded={async () => { await fetchInvoice(); await fetchPayments(); }}
      />
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center text-sm">
    <span className="text-gray-600">{label}</span>
    <span className="font-medium text-[#2B2D42]">{value}</span>
  </div>
);

export default InvoiceEdit;
